import { useEffect, useRef } from 'react';
import { FeatureState } from './features';
import { publishKatalMetric } from '../components/katalAnalytics';
import { FEATURE_FLAG_LOAD_METRIC } from '../utils/constants';

/**
 * Emits a one-time-per-landing analytics metric for every server-enabled feature flag the current
 * session is dialed into, so engagement/conversion can be segmented by dial-up cohort in a dashboard.
 *
 * Reads the raw server allowlist (`feature.value`) directly rather than going through
 * `featureIsEnabled`, so `?features=` URL overrides (used by devs/QA to preview flags) are
 * intentionally excluded -- only genuine server dial-up cohorts are recorded.
 *
 * One metric is published per enabled flag with key `FEATURE_FLAG_LOAD_METRIC` and value = flag name.
 * A session in no flags publishes nothing (control is inferred in the dashboard). Firing is guarded
 * so it happens at most once per mount, even under React 18 StrictMode's double-invoked effects.
 *
 * @param features Server feature state from `useFeatures()`; null while loading (nothing is fired).
 */
export function useFeatureFlagLoadMetrics(features: FeatureState[] | null): void {
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (!features || hasFiredRef.current) {
      return;
    }
    hasFiredRef.current = true;
    features
      .filter((feature) => feature.value)
      .forEach((feature) => publishKatalMetric(FEATURE_FLAG_LOAD_METRIC, feature.feature));
  }, [features]);
}
